import { HelpCircle } from "@untitledui/icons";
import type { ReactNode, Ref } from "react";
import type { LabelProps as AriaLabelProps } from "react-aria-components";
import { Label as AriaLabel } from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

interface LabelProps extends AriaLabelProps {
	children: ReactNode;
	isRequired?: boolean;
	tooltip?: string;
	tooltipDescription?: string;
	ref?: Ref<HTMLLabelElement>;
}

export const Label = ({
	isRequired,
	tooltip,
	tooltipDescription,
	className,
	...props
}: LabelProps) => {
	return (
		<AriaLabel
			// Used for conditionally hiding/showing the label element via CSS:
			// <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
			// or
			// <Input label="Visible only on mobile" className="lg:label:hidden" />
			data-label="true"
			{...props}
			className={cx(
				"flex cursor-default items-center gap-0.5 font-medium text-gray-900 text-sm dark:text-gray-50",
				className
			)}
		>
			{props.children}

			<span
				className={cx(
					"hidden text-brand-tertiary",
					isRequired && "block",
					typeof isRequired === "undefined" && "group-required:block"
				)}
			>
				*
			</span>

			{tooltip && (
				<Tooltip
					description={tooltipDescription}
					placement="top"
					title={tooltip}
				>
					<TooltipTrigger
						// `TooltipTrigger` inherits the disabled state from the parent form field
						// but we don't that. We want the tooltip be enabled even if the parent
						// field is disabled.
						className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
						isDisabled={false}
					>
						<HelpCircle className="size-4" />
					</TooltipTrigger>
				</Tooltip>
			)}
		</AriaLabel>
	);
};

Label.displayName = "Label";
